import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export interface FeedStats {
  queued: number;
  sending: number;
  sent_today: number;
  failed: number;
  dead: number;
  oldest_queued_at: string | null;
  total_submissions: number;
}

export interface OutboxRow {
  id: string;
  submission_id: string;
  event_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  last_error: string | null;
  last_http_status: number | null;
  created_at: string;
  sent_at: string | null;
}

async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function assertAdmin(userId: string) {
  const db = await admin();
  const { data, error } = await db.from("user_roles").select("role").eq("user_id", userId);
  if (error) throw new Error(error.message);
  if (!(data ?? []).some((r) => r.role === "admin")) throw new Error("Admin only");
  return db;
}

export const getFeedStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<FeedStats> => {
    const db = await assertAdmin(context.userId);

    const countBy = async (status: string) => {
      const { count } = await db
        .from("feed_outbox")
        .select("id", { count: "exact", head: true })
        .eq("status", status);
      return count ?? 0;
    };

    const startOfDay = new Date();
    startOfDay.setUTCHours(0, 0, 0, 0);

    const [queued, sending, failed, dead] = await Promise.all([
      countBy("queued"),
      countBy("sending"),
      countBy("failed"),
      countBy("dead"),
    ]);

    const { count: sentToday } = await db
      .from("feed_outbox")
      .select("id", { count: "exact", head: true })
      .eq("status", "sent")
      .gte("sent_at", startOfDay.toISOString());

    const { data: oldest } = await db
      .from("feed_outbox")
      .select("created_at")
      .eq("status", "queued")
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    const { count: totalSubs } = await db
      .from("editor_video_submissions")
      .select("id", { count: "exact", head: true })
      .is("deleted_at", null);

    return {
      queued,
      sending,
      sent_today: sentToday ?? 0,
      failed,
      dead,
      oldest_queued_at: oldest?.created_at ?? null,
      total_submissions: totalSubs ?? 0,
    };
  });

export const listOutbox = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z
      .object({ status: z.string().optional().nullable(), limit: z.number().max(200).optional() })
      .parse(data ?? {}),
  )
  .handler(async ({ context, data }) => {
    const db = await assertAdmin(context.userId);
    let q = db
      .from("feed_outbox")
      .select(
        "id, submission_id, event_type, status, attempts, max_attempts, next_attempt_at, last_error, last_http_status, created_at, sent_at",
      )
      .order("created_at", { ascending: false })
      .limit(data.limit ?? 50);
    if (data.status && data.status !== "all") q = q.eq("status", data.status);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return (rows ?? []) as unknown as OutboxRow[];
  });

/** Enqueue every non-deleted submission. Idempotent: skips ones already pending. */
export const backfillFeed = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const db = await assertAdmin(context.userId);
    const { data, error } = await db.rpc("feed_backfill_all");
    if (error) throw new Error(error.message);
    return { enqueued: (data as number) ?? 0 };
  });

export const runFeedSenderNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { runFeedSender } = await import("./feed-sender.server");
    return await runFeedSender();
  });

export const retryFeedRows = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ status: z.enum(["failed", "dead"]) }).parse(data))
  .handler(async ({ context, data }) => {
    const db = await assertAdmin(context.userId);
    const patch: Record<string, unknown> = {
      status: "queued",
      next_attempt_at: new Date().toISOString(),
      last_error: null,
      leased_at: null,
    };
    if (data.status === "dead") patch.attempts = 0;
    const { data: rows, error } = await db
      .from("feed_outbox")
      .update(patch as never)
      .eq("status", data.status)
      .select("id");
    if (error) throw new Error(error.message);
    return { requeued: rows?.length ?? 0 };
  });
